import {
  DereferencedTargetingDataType,
  TargetingDataType,
  VisitorReference,
} from 'src/targeting';
import { ListUtilities } from 'src/utilities/listUtilities';
import {
  DereferenceTargetingDataResultType,
  ExistingDataType,
  ExistingNestedDataType,
  FieldParametersType,
  KameleoonData,
  KameleoonVisitorDataType,
  MutAddUnsentDataType,
  MutUpdateDataParametersType,
  NestedFieldParametersType,
  ReferenceParametersType,
  UpdateCommonDataParametersType,
  UpdateConversionParametersType,
  UpdateCustomDataParametersType,
  UpdatePageViewParametersType,
  UpdatePersonalizationParametersType,
  UpdateTargetSegmentParametersType,
  UpdateVisitsDataParametersType,
  VisitDataType,
} from './types';
import { CustomData } from './customData';
import { VisitsData } from './visitsData';
import { Conversion } from './conversion';
import { PageView } from './pageView';
import { DataInfoType } from 'src/storage';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';
import { Personalization } from 'src/kameleoonData/personalization';
import { TargetedSegment } from 'src/kameleoonData/targetedSegment';

interface IDataProcessor {
  mutUpdateData({
    visitorCode,
    mutData,
    dataItem,
  }: MutUpdateDataParametersType): number;
  mutCleanupData(
    mutData: TargetingDataType,
    infoData: DataInfoType,
  ): number | null;
  createReference({
    mutData,
    visitorCode,
    linkedVisitor,
    infoData,
  }: ReferenceParametersType): void;
  dereferenceData(
    data: TargetingDataType,
    visitorCode: string,
  ): DereferenceTargetingDataResultType;
  mutAddUnsentData({
    mutData,
    visitorCode,
    dataType,
  }: MutAddUnsentDataType): void;
}

export class DataProcessor implements IDataProcessor {
  private cleanupInterval: number | null;
  private packageInfo: ExternalPackageInfoType;

  constructor(
    cleanupInterval: number | null = null,
    packageInfo: ExternalPackageInfoType,
  ) {
    this.cleanupInterval = cleanupInterval;
    this.packageInfo = packageInfo;
  }

  public mutUpdateData({
    infoData,
    visitorCode,
    mutData,
    dataItem,
  }: MutUpdateDataParametersType): number {
    let { visitorReference, data, isReference } = this.dereferenceData(
      mutData,
      visitorCode,
    );

    if (
      this.packageInfo.isServer &&
      isReference &&
      !mutData[visitorReference]
    ) {
      if (infoData.mappingIdentifiers) {
        delete infoData.mappingIdentifiers[visitorCode];
      }
      delete mutData[visitorCode];
      visitorReference = visitorCode;
    }

    const expirationTime = this.cleanupInterval
      ? Date.now() + this.cleanupInterval
      : 0;

    switch (dataItem.data.type) {
      case KameleoonData.CustomData: {
        this.updateCustomData({
          expirationTime,
          customData: dataItem as CustomData,
          visitorCode: visitorReference,
          mutData: data,
        });

        break;
      }
      case KameleoonData.PageView: {
        this.updatePageView({
          expirationTime,
          pageView: dataItem as PageView,
          visitorCode: visitorReference,
          mutData: data,
        });

        break;
      }
      case KameleoonData.Conversion: {
        this.updateConversion({
          expirationTime,
          conversion: dataItem as Conversion,
          visitorCode: visitorReference,
          mutData: data,
        });

        break;
      }
      case KameleoonData.VisitsData: {
        this.updateVisitsData({
          expirationTime,
          visitsData: dataItem as VisitsData,
          visitorCode: visitorReference,
          mutData: data,
        });
        break;
      }
      case KameleoonData.Personalization: {
        this.updatePersonalization({
          expirationTime,
          personalization: dataItem as Personalization,
          visitorCode: visitorReference,
          mutData: data,
        });
        break;
      }
      case KameleoonData.TargetedSegment: {
        this.updateTargetedSegment({
          expirationTime,
          targetedSegment: dataItem as TargetedSegment,
          visitorCode: visitorReference,
          mutData: data,
        });
        break;
      }
      default: {
        this.updateCommonData({
          expirationTime,
          commonData: dataItem,
          visitorCode: visitorReference,
          mutData: data,
        });
      }
    }

    return expirationTime;
  }

  public mutCleanupData(
    mutData: TargetingDataType,
    infoData: DataInfoType,
  ): number | null {
    if (!this.cleanupInterval) {
      return null;
    }

    let nextCleanupTime = 0;

    for (const [visitorCode, data] of Object.entries(mutData)) {
      if (typeof data === 'string') {
        this.removeReference({
          mutData,
          visitorCode,
          linkedVisitor: data,
          infoData,
        });

        continue;
      }

      let closestCleanupTime = 0;

      for (const key of Object.keys(data)) {
        const dataKey = key as KameleoonData;

        let time;

        switch (dataKey) {
          case KameleoonData.CustomData:
          case KameleoonData.PageView:
          case KameleoonData.Conversion:
          case KameleoonData.Personalization: {
            time = this.deleteNestedExpiredField(data, dataKey);
            break;
          }
          default: {
            time = this.deleteExpiredField(data, dataKey);
          }
        }

        if (!closestCleanupTime || (time && time < closestCleanupTime)) {
          closestCleanupTime = time;
        }
      }

      if (!Object.keys(mutData[visitorCode]).length) {
        delete mutData[visitorCode];
        if (this.packageInfo.isServer) {
          infoData.mappingIdentifiers ??= {};
          delete infoData.mappingIdentifiers[visitorCode];
        }
      } else {
        if (
          !nextCleanupTime ||
          (closestCleanupTime && closestCleanupTime < nextCleanupTime)
        ) {
          nextCleanupTime = closestCleanupTime;
        }
      }
    }

    return nextCleanupTime;
  }

  public mutAddUnsentData({
    mutData,
    visitorCode,
    dataType,
  }: MutAddUnsentDataType): void {
    const unsentData = mutData.unsentData[visitorCode];

    if (!unsentData || !unsentData.length) {
      mutData.unsentData[visitorCode] = [dataType];
    } else {
      const set = new Set([...unsentData]);
      set.add(dataType);

      mutData.unsentData[visitorCode] = Array.from(set);
    }
  }

  public createReference({
    mutData,
    visitorCode,
    linkedVisitor,
  }: ReferenceParametersType): void {
    // --- Note ---
    // Provided(linked) visitor is now pointing to the visitor code
    mutData[linkedVisitor] = visitorCode;
  }

  public removeReference({
    mutData,
    visitorCode,
    linkedVisitor,
    infoData,
  }: ReferenceParametersType): void {
    if (this.packageInfo.isServer) {
      if (!mutData[linkedVisitor]) {
        delete mutData[visitorCode];
        if (infoData.mappingIdentifiers) {
          delete infoData.mappingIdentifiers[visitorCode];
        }
      }
    }
  }

  private updatePageView({
    visitorCode,
    mutData,
    pageView,
    expirationTime,
  }: UpdatePageViewParametersType): void {
    const { data } = pageView;

    const isExist =
      mutData[visitorCode] &&
      KameleoonData.PageView in mutData[visitorCode] &&
      data.urlAddress in mutData[visitorCode][KameleoonData.PageView]!;

    const parameters: NestedFieldParametersType = {
      expirationTime,
      visitorCode,
      data: mutData,
      key: KameleoonData.PageView,
      nestedKey: data.urlAddress,
      value: data,
    };

    if (isExist) {
      const existingData =
        mutData[visitorCode][KameleoonData.PageView]![data.urlAddress];
      const existingTimestamps = existingData.timestamps;

      // --- Note ---
      // Merging existing timestamps with added
      for (const existingTimestamp of existingTimestamps) {
        const { time: existingTime } = existingTimestamp;

        if (data.timestamps.some(({ time }) => time === existingTime)) {
          continue;
        }

        const index = data.timestamps.findIndex(
          ({ time }) => time >= existingTime,
        );

        if (index === -1) {
          data.timestamps.push(existingTimestamp);
        } else {
          data.timestamps.splice(index, 0, existingTimestamp);
        }
      }

      this.updateNestedField(parameters);
    } else {
      this.createNestedField(parameters);
    }
  }

  private updateCustomData({
    visitorCode,
    customData,
    mutData,
    expirationTime,
  }: UpdateCustomDataParametersType): void {
    const { data } = customData;
    const isExist =
      mutData[visitorCode] && KameleoonData.CustomData in mutData[visitorCode];

    const parameters: NestedFieldParametersType = {
      expirationTime,
      visitorCode,
      data: mutData,
      key: KameleoonData.CustomData,
      nestedKey: data.index.toString(),
      value: data,
    };

    if (isExist) {
      this.updateNestedField(parameters);
    } else {
      this.createNestedField(parameters);
    }
  }

  private updateConversion({
    conversion,
    mutData,
    visitorCode,
    expirationTime,
  }: UpdateConversionParametersType): void {
    const { data } = conversion;
    const isExist =
      mutData[visitorCode] && KameleoonData.Conversion in mutData[visitorCode];

    const parameters: NestedFieldParametersType = {
      expirationTime,
      visitorCode,
      data: mutData,
      key: KameleoonData.Conversion,
      nestedKey: data.id.toString(),
      value: data,
    };

    if (isExist) {
      this.updateNestedField(parameters);
    } else {
      this.createNestedField(parameters);
    }
  }

  private updateVisitsData({
    visitorCode,
    mutData,
    visitsData,
    expirationTime,
  }: UpdateVisitsDataParametersType): void {
    const { data } = visitsData;
    const isExist =
      mutData[visitorCode] && KameleoonData.VisitsData in mutData[visitorCode];

    const parameters: FieldParametersType = {
      expirationTime,
      data: mutData,
      key: KameleoonData.VisitsData,
      value: data,
      visitorCode,
    };

    if (isExist) {
      const existingVisits = mutData[visitorCode][KameleoonData.VisitsData]!;

      // --- Note ---
      // We are preserving the order of visits
      existingVisits.visits.forEach((visit) => {
        ListUtilities.insertVisitInOrderedListMutably({
          list: data.visits as VisitDataType[],
          visit: visit as VisitDataType,
        });
      });

      const visitNumber = Math.max(
        data.visitNumber ?? data.visits.length - 1,
        data.visits.length - 1,
        existingVisits.visitNumber ?? 0,
      );

      this.updateField({
        ...parameters,
        value: {
          ...data,
          visits: data.visits,
          visitNumber: visitNumber,
        },
      });
    } else {
      this.createField(parameters);
    }
  }

  private updatePersonalization({
    visitorCode,
    mutData,
    personalization,
    expirationTime,
  }: UpdatePersonalizationParametersType): void {
    const { data } = personalization;
    const isExist =
      mutData[visitorCode] &&
      KameleoonData.Personalization in mutData[visitorCode];

    const parameters: NestedFieldParametersType = {
      expirationTime,
      visitorCode,
      data: mutData,
      key: KameleoonData.Personalization,
      nestedKey: data.id.toString(),
      value: data,
    };

    if (isExist) {
      this.updateNestedField(parameters);
    } else {
      this.createNestedField(parameters);
    }
  }

  private updateTargetedSegment({
    visitorCode,
    mutData,
    targetedSegment,
    expirationTime,
  }: UpdateTargetSegmentParametersType): void {
    const { data } = targetedSegment;
    const isExist =
      mutData[visitorCode] &&
      KameleoonData.TargetedSegment in mutData[visitorCode];

    const parameters: NestedFieldParametersType = {
      expirationTime,
      visitorCode,
      data: mutData,
      key: KameleoonData.TargetedSegment,
      nestedKey: data.id.toString(),
      value: data,
    };

    if (isExist) {
      this.updateNestedField(parameters);
    } else {
      this.createNestedField(parameters);
    }
  }

  private updateCommonData({
    visitorCode,
    mutData,
    commonData,
    expirationTime,
  }: UpdateCommonDataParametersType): void {
    const { data } = commonData;
    const isExist = mutData[visitorCode] && data.type in mutData[visitorCode];

    const parameters: FieldParametersType = {
      expirationTime,
      data: mutData,
      key: data.type,
      visitorCode,
      value: data as FieldParametersType['value'],
    };

    if (isExist) {
      this.updateField(parameters);
    } else {
      this.createField(parameters);
    }
  }

  public dereferenceData(
    data: TargetingDataType,
    visitorCode: string,
  ): DereferenceTargetingDataResultType {
    // --- Note ---
    // Visitor reference can not point to another visitor reference
    if (typeof data[visitorCode] === 'string') {
      return {
        visitorReference: data[visitorCode] as VisitorReference,
        data: data as DereferencedTargetingDataType,
        isReference: true,
      };
    }

    return {
      visitorReference: visitorCode,
      data: data as DereferencedTargetingDataType,
      isReference: false,
    };
  }

  private deleteExpiredField(
    data: Omit<
      KameleoonVisitorDataType,
      | KameleoonData.Conversion
      | KameleoonData.CustomData
      | KameleoonData.PageView
      | KameleoonData.Personalization
      | KameleoonData.TargetedSegment
    >,
    key: KameleoonData,
  ): number {
    if (!data) {
      return 0;
    }

    const dataKey = key as keyof typeof data;
    const isExpired = data[dataKey]!.expirationTime < Date.now();

    if (!isExpired) {
      return data[dataKey]!.expirationTime;
    }

    delete data[dataKey];

    return 0;
  }

  private deleteNestedExpiredField(
    data: KameleoonVisitorDataType,
    key: KameleoonData,
  ): number {
    if (!data[key]) {
      return 0;
    }

    const existingData = data[key] as ExistingNestedDataType;

    let closestCleanupTime;

    for (const [nestedKey, value] of Object.entries(existingData)) {
      if (!value) {
        continue;
      }

      const isExpired = value.expirationTime < Date.now();

      if (isExpired) {
        delete existingData[nestedKey];
      } else {
        if (!closestCleanupTime || value.expirationTime < closestCleanupTime) {
          closestCleanupTime = value.expirationTime;
        }
      }
    }

    if (!Object.keys(existingData).length) {
      delete data[key];
    }

    return closestCleanupTime;
  }

  private updateField({
    key,
    value,
    data,
    visitorCode,
    expirationTime,
  }: FieldParametersType): void {
    (data[visitorCode][key] as ExistingDataType<typeof key>) = {
      ...value,
      expirationTime,
    };
  }

  private createField({
    key,
    value,
    data,
    visitorCode,
    expirationTime,
  }: FieldParametersType): void {
    data[visitorCode] = {
      ...data[visitorCode],
      [key]: { ...value, expirationTime },
    };
  }

  private updateNestedField({
    key,
    nestedKey,
    value,
    data,
    visitorCode,
    expirationTime,
  }: NestedFieldParametersType): void {
    (data[visitorCode][key] as ExistingNestedDataType)[nestedKey] = {
      ...value,
      expirationTime,
    };
  }

  private createNestedField({
    key,
    nestedKey,
    value,
    data,
    visitorCode,
    expirationTime,
  }: NestedFieldParametersType): void {
    if (!data[visitorCode]?.[key]) {
      data[visitorCode] = {
        ...data[visitorCode],
        [key]: {
          [nestedKey]: { ...value, expirationTime },
        },
      };
    } else {
      data[visitorCode] = {
        ...data[visitorCode],
        [key]: {
          ...data[visitorCode][key],
          [nestedKey]: { ...value, expirationTime },
        },
      };
    }
  }
}
